import { Button } from '@wds/button';
import { ButtonGroup } from '@wds/button-group';
import { IconChevronLeft, IconChevronRight } from '@wds/icons';
import { SelectMenu } from '@wds/select-menu';
import { Typography } from '@wds/typography';
import { createMemoStyles, visuallyHidden, clsx, safeRest } from '@wds/utils';
import React from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}
var useStyles = createMemoStyles(function (theme) {
  var _a, _b, _c;
  var spacing = theme.spacing,
    breakpoints = theme.breakpoints;
  return {
    wrenchPagination: (_a = {
      width: '100%',
      textAlign: 'center'
    }, _a["@media (min-width: ".concat(breakpoints.lg, "px)")] = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    }, _a),
    wrenchPaginationAddOn: (_b = {
      display: 'block',
      marginBottom: theme.spacing[4]
    }, _b["@media (min-width: ".concat(breakpoints.lg, "px)")] = {
      display: 'inline-block',
      margin: [0, spacing[1]],
      textAlign: 'auto',
      marginBottom: 0,
      paddingLeft: spacing[3] - 4
    }, _b),
    wrenchPaginationPage: (_c = {}, _c["@media (min-width: ".concat(breakpoints.md, "px)")] = {
      display: 'inline-block'
    }, _c),
    wrenchPaginationHidden: __assign({}, visuallyHidden)
  };
});
var Pagination = function (_a) {
  var count = _a.count,
    _b = _a.hasPageSelect,
    hasPageSelect = _b === void 0 ? false : _b,
    _c = _a.hasRowsPerPage,
    hasRowsPerPage = _c === void 0 ? false : _c,
    _d = _a.labelLegend,
    labelLegend = _d === void 0 ? function (firstRowOnPage, lastRowOnPage, totalRows) {
      return "Showing ".concat(firstRowOnPage, " \u2013 ").concat(lastRowOnPage, " of ").concat(totalRows);
    } : _d,
    _e = _a.labelJumpToPage,
    labelJumpToPage = _e === void 0 ? function (page) {
      return "Page ".concat(page);
    } : _e,
    _f = _a.labelRowsPerPage,
    labelRowsPerPage = _f === void 0 ? function (page) {
      return "".concat(page, " per page");
    } : _f,
    onChangePage = _a.onChangePage,
    onChangeRowsPerPage = _a.onChangeRowsPerPage,
    page = _a.page,
    rowsPerPage = _a.rowsPerPage,
    _g = _a.placement,
    placement = _g === void 0 ? 'bottom-left' : _g,
    rest = __rest(_a, ["count", "hasPageSelect", "hasRowsPerPage", "labelLegend", "labelJumpToPage", "labelRowsPerPage", "onChangePage", "onChangeRowsPerPage", "page", "rowsPerPage", "placement"]);
  var _h = useStyles(),
    wrenchPagination = _h.wrenchPagination,
    wrenchPaginationAddOn = _h.wrenchPaginationAddOn,
    wrenchPaginationPage = _h.wrenchPaginationPage,
    wrenchPaginationHidden = _h.wrenchPaginationHidden;
  var paginationPageAddOnClasses = clsx(wrenchPaginationAddOn, wrenchPaginationPage);
  var currentPage = page + 1;
  var firstRowOnPage = count === 0 ? 0 : page * rowsPerPage + 1;
  var lastRowOnPage = Math.min(count, currentPage * rowsPerPage);
  var totalRows = count;
  var totalPages = Math.ceil(count / rowsPerPage);
  var prevPageDisabled = page === 0;
  var nextPageDisabled = page >= Math.ceil(totalRows / rowsPerPage) - 1;
  var handleBackButtonClick = function (event) {
    if (onChangePage) {
      onChangePage(event, page - 1);
    }
  };
  var handleNextButtonClick = function (event) {
    if (onChangePage) {
      onChangePage(event, page + 1);
    }
  };
  var handleJumpToPage = function (event) {
    var el = event.currentTarget;
    var dataVal = el.dataset.value;
    var optionValue = String(dataVal);
    var goToPage = parseInt(optionValue, 10) - 1;
    if (onChangePage) {
      onChangePage(event, goToPage);
    }
  };
  var handleChangeRowsPerPage = function (event) {
    var el = event.currentTarget;
    var optionValue = String(el.dataset.value);
    var newRowsPerPage = parseInt(optionValue, 10);
    if (onChangeRowsPerPage) {
      onChangeRowsPerPage(event, newRowsPerPage);
    }
  };
  return React.createElement("div", __assign({
    className: wrenchPagination,
    "aria-label": "pagination"
  }, safeRest(rest)), React.createElement("span", {
    className: wrenchPaginationAddOn
  }, React.createElement(Typography, {
    component: "div",
    variant: "bodySm",
    color: "darkMuted",
    align: "center"
  }, labelLegend(firstRowOnPage, lastRowOnPage, totalRows))), hasRowsPerPage && React.createElement("span", {
    className: paginationPageAddOnClasses
  }, React.createElement(SelectMenu, {
    placement: placement,
    value: "".concat(rowsPerPage),
    onChange: handleChangeRowsPerPage
  }, React.createElement(SelectMenu.Option, {
    value: "10"
  }, labelRowsPerPage(10)), React.createElement(SelectMenu.Option, {
    value: "25"
  }, labelRowsPerPage(25)), React.createElement(SelectMenu.Option, {
    value: "50"
  }, labelRowsPerPage(50)), React.createElement(SelectMenu.Option, {
    value: "100"
  }, labelRowsPerPage(100)))), hasPageSelect && React.createElement("span", {
    className: paginationPageAddOnClasses
  }, React.createElement(SelectMenu, {
    placement: placement,
    value: "".concat(currentPage),
    onChange: handleJumpToPage
  }, __spreadArray([], Array(totalPages), true).map(function (_, i) {
    return React.createElement(SelectMenu.Option, {
      value: "".concat(i + 1),
      key: "option-".concat(i + 1)
    }, labelJumpToPage(i + 1));
  }))), React.createElement("span", {
    className: wrenchPaginationAddOn,
    role: "navigation"
  }, React.createElement(ButtonGroup, null, React.createElement(Button, {
    color: "muted",
    onClick: handleBackButtonClick,
    disabled: prevPageDisabled,
    label: !prevPageDisabled ? "Go to page ".concat(currentPage - 1) : 'End of page'
  }, React.createElement(IconChevronLeft, {
    title: "Previous Page"
  })), React.createElement("div", {
    className: wrenchPaginationHidden
  }, "Current page ", currentPage), React.createElement(Button, {
    color: "muted",
    onClick: handleNextButtonClick,
    disabled: nextPageDisabled,
    label: !nextPageDisabled ? "Go to page ".concat(currentPage + 1) : 'End of page'
  }, React.createElement(IconChevronRight, {
    title: "Next Page"
  })))));
};
export { Pagination };